import React from "react";
import { useQuery } from "@tanstack/react-query";
import CircularProgress from "@mui/material/CircularProgress";

import DocumentMarkdownView from "@/components/common/DocumentMarkdownView";
import { BASE_API_URL } from "@/constants";
import fpFetch from "@/services/fpFetch";
import HtmlIframeView from "./HtmlIframeView";

type Props = { fileId: string };

const HtmlViewer = ({ fileId }: Props) => {
  // URL toggle: ?htmlview=iframe → sandboxed iframe of the original HTML.
  // Default → markdown-from-html derivative rendered as constrained markdown.
  // Lets a reviewer flip rendering modes via the address bar without touching
  // code; both branches consume content the backend already prepared, so the
  // markdown branch matches what the LLM consumed during ingestion.
  const showIframe =
    new URLSearchParams(window.location.search).get("htmlview") === "iframe";

  const { data, isLoading, error } = useQuery({
    queryKey: ["html-blob", fileId, showIframe ? "html" : "markdown"],
    queryFn: async () => {
      const url = showIframe
        ? `${BASE_API_URL}/userData/fp0065?fileId=${fileId}&v=cacheBust8`
        : `${BASE_API_URL}/userData/fp0065?fileId=${fileId}&format=markdown&v=cacheBust8`;
      const response = await fpFetch(url);
      if (!response.ok) {
        throw new Error(
          `Failed to load HTML content: ${response.status} ${response.statusText}`
        );
      }
      return await response.text();
    },
    enabled: Boolean(fileId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <CircularProgress size={32} />
      </div>
    );
  }
  if (error || data == null) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-semibold">
            Unable to load HTML content
          </div>
        </div>
      </div>
    );
  }

  if (showIframe) {
    return <HtmlIframeView html={data} />;
  }
  return <DocumentMarkdownView markdown={data} />;
};

export default HtmlViewer;
